import React, { memo } from "react";
import Modal from "react-modal";
import { IoClose } from "react-icons/io5";

import styles from "./summary.module.scss";
import { SHOW_NOTABLE_QUOTES } from "./hiddenSections";

// Display order shared with the app + add/edit forms. The last three sit
// behind "Show more" in the app, but the admin view lists them all.
const SUMMARY_SECTIONS = [
  { key: "aboutTheBook", label: "About the Book" },
  { key: "whyItMatters", label: "Why This Book Matters" },
  { key: "KeyIdeas", label: "Key Ideas" },
  // Hidden, not removed — see hiddenSections.js.
  ...(SHOW_NOTABLE_QUOTES ? [{ key: "notableQuotes", label: "Notable Quotes" }] : []),
  { key: "actionableInsights", label: "Actionable Insights" },
  { key: "shouldReadFor", label: "Who Should Read It" },
  { key: "reflectionQuestions", label: "Questions This Book Forces You to Ask" },
];

const getSectionContent = (summary, key) => {
  if (!summary) {
    return "";
  }

  if (key === "aboutTheBook") {
    return summary.aboutTheBook || summary.bookSummary || summary.aboutBook || "";
  }

  if (key === "KeyIdeas") {
    return summary.KeyIdeas || summary.keyIdeas || summary.ideas || "";
  }

  if (key === "notableQuotes") {
    return summary.notableQuotes || summary.curatedQuotes || "";
  }

  return summary[key] || "";
};

/**
 * Two views of one summary, from the list's two columns.
 *
 * OVERVIEW is every section above — About the Book, Why This Book Matters,
 * Key Ideas and the rest. It used to be narrowed to About the Book alone,
 * which is only the first of seven.
 *
 * SUMMARY is the chapters. Those are not in the list's payload — a summary
 * carries every chapter's full text, and sending that for fifty rows is the
 * reason the list projects aggregates instead — so the caller fetches the one
 * summary when the popup opens and passes them in.
 *
 * [only] still narrows the sections by key; nothing uses it today.
 */
const ViewSummaryPopup = ({ isOpen, onClose, summaryValues, only, title, chapters, loading }) => {
  const sections = only?.length
    ? SUMMARY_SECTIONS.filter((section) => only.includes(section.key))
    : SUMMARY_SECTIONS;

  const showChapters = Array.isArray(chapters) || loading;


  return (
    <Modal
      isOpen={isOpen}
      onRequestClose={onClose}
      className="modal_body_md"
      overlayClassName="modal_overlay"
    >
      <div className={`box_wrapper ${styles.viewSummaryWrapper}`}>
        <div className={styles.viewSummaryHeader}>
          <div>
            <h4 className="mb-1">{title || "Summary Content Details"}</h4>
          </div>
          <button
            type="button"
            className={styles.viewSummaryCloseButton}
            onClick={onClose}
            aria-label="Close summary details popup"
          >
            <IoClose size={22} />
          </button>
        </div>

        <div className={`modal_content_md ${styles.summaryDetailsContent}`}>
          {showChapters ? (
            loading ? (
              <p className={`mb-0 ${styles.summaryEmptyText}`}>Loading chapters…</p>
            ) : chapters.length === 0 ? (
              <p className={`mb-0 ${styles.summaryEmptyText}`}>This summary has no chapters yet.</p>
            ) : (
              chapters.map((chapter, index) => (
                <div key={chapter._id || index} className={styles.summarySection}>
                  <div className={styles.summarySectionHeadingRow}>
                    <span className={styles.summarySectionCounter}>{String(index + 1).padStart(2, "0")}</span>
                    <h6 className={styles.summarySectionHeading}>
                      {chapter.chapterName || `Chapter ${index + 1}`}
                    </h6>
                  </div>
                  <div className={styles.summarySectionBody}>
                    {chapter.content ? (
                      <div
                        className={styles.summaryRenderedContent}
                        dangerouslySetInnerHTML={{ __html: chapter.content }}
                      />
                    ) : (
                      <p className={`mb-0 ${styles.summaryEmptyText}`}>No content available.</p>
                    )}
                  </div>
                </div>
              ))
            )
          ) : (
          sections.map((section, index) => {
            const content = getSectionContent(summaryValues, section.key);

            return (
              <div key={section.key} className={styles.summarySection}>
                <div className={styles.summarySectionHeadingRow}>
                  {sections.length > 1 && (
                    <span className={styles.summarySectionCounter}>{String(index + 1).padStart(2, "0")}</span>
                  )}
                  <h6 className={styles.summarySectionHeading}>{section.label}</h6>
                </div>
                <div className={styles.summarySectionBody}>
                  {content ? (
                    <div
                      className={styles.summaryRenderedContent}
                      dangerouslySetInnerHTML={{ __html: content }}
                    />
                  ) : (
                    <p className={`mb-0 ${styles.summaryEmptyText}`}>No content available.</p>
                  )}
                </div>
              </div>
            );
          }))}
        </div>
      </div>
    </Modal>
  );
};

export default memo(ViewSummaryPopup);
